import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, AlertTriangle } from 'lucide-react';
import {
  PlatformSettingConsoleView,
  PlatformSettingDefinitionView,
  getPlatformSettingsConsole,
} from '../../../api/Platform/settings';
import PlatformSettingDialog from './PlatformSettingDialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Button from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { PageLayout } from '../../../components/ui/PageLayout';
import { apiErrorsOf } from '../../../utils/requestHandling';

/**
 * 🔴 OWNER DECISION E12 (2026-08-20) — the Operator console for business configuration that has
 * LEFT `appsettings.json`.
 *
 * <p>It is deliberately ONE page for the whole class rather than a control bolted onto whichever
 * feature screen each setting happens to belong to. The class already spans subscriptions, payroll
 * compliance, engagement, AI quota and billing; scattering it would produce five inconsistent
 * tristate editors and no single place to answer "what is this tenant actually running on".</p>
 *
 * <p>Two sections, because there are exactly two levels and they are not the same kind of act.
 * Changing a PLATFORM DEFAULT moves the whole fleet and has no compiled fallback behind it any
 * more; changing a COMPANY OVERRIDE moves one tenant. The page states which one you are about to
 * do, every time.</p>
 *
 * <p>🔴 The "not seeded" badge is not decoration. With the configuration keys deleted, a missing
 * seed row means the product is running on a last-ditch constant that no operator can edit — the
 * exact E12 failure mode. It is surfaced here because the alternative is an identical-looking
 * number that silently cannot be changed.</p>
 */

interface EditTarget {
  definition: PlatformSettingDefinitionView;
  companyId: number | null;
  companyName: string | null;
  currentValue: string | null;
  inheritedValue: string;
  currentNote: string | null;
}

const PlatformSettings: React.FC = () => {
  const { t } = useTranslation();
  const [console_, setConsole] = useState<PlatformSettingConsoleView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [target, setTarget] = useState<EditTarget | null>(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getPlatformSettingsConsole();
      if (result.status === 200 && result.payload) {
        setConsole(result.payload);
      } else {
        setLoadError(apiErrorsOf(result)[0]?.description || t('platformConfig.loadError'));
      }
    } catch (caught) {
      // 🔴 A non-2xx REJECTS (`utils/requestHandling` interceptor) and `ToActionResult` maps
      // `Result.Status` onto the HTTP status, so a 403 "this console is operator-only" never reaches
      // the resolved branch above. The reason rides on the rejected value; `apiErrorsOf` reads it out
      // of whatever shape it arrived in and never throws.
      setLoadError(apiErrorsOf(caught)[0]?.description || t('platformConfig.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderValue = (definition: PlatformSettingDefinitionView, raw: string) =>
    definition.unlimitedSentinel && raw.toLowerCase() === definition.unlimitedSentinel.toLowerCase()
      ? t('platformConfig.noLimit')
      : raw;

  return (
    <PageLayout
      title={t('platformConfig.title')}
      description={t('platformConfig.intro')}
      icon={SlidersHorizontal}
      accentColor="slate"
    >
      <div className="w-full overflow-y-auto">
        {loading ? (
          <div className="flex justify-center p-6">
            <Spinner size="lg" />
          </div>
        ) : loadError ? (
          <p role="alert" className="p-6 text-sm text-destructive">{loadError}</p>
        ) : console_ ? (
          <div className="flex flex-col gap-10 pb-6">

            {/* ── Level 1: the platform default ────────────────────────────── */}
            <section className="flex flex-col gap-1">
              <h2 className="text-lg font-medium text-foreground">{t('platformConfig.platformHeading')}</h2>
              <p className="max-w-[70ch] text-sm text-muted-foreground">{t('platformConfig.platformSubheading')}</p>

              <ul className="mt-4 divide-y divide-border border-y border-border">
                {console_.definitions.map(definition => (
                  <li key={definition.key} className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 py-4">
                    <div className="min-w-0 max-w-[60ch] space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{t(definition.labelKey)}</span>
                        {!definition.platformSeeded && (
                          <StatusBadge variant="warning" size="sm">{t('platformConfig.notSeeded')}</StatusBadge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{t(definition.descriptionKey)}</p>
                      {definition.consequenceKey && (
                        <p className="flex items-start gap-2 pt-1 text-xs leading-relaxed text-muted-foreground">
                          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
                          <span>{t(definition.consequenceKey)}</span>
                        </p>
                      )}
                      {!definition.platformSeeded && (
                        <p className="pt-1 text-xs leading-relaxed text-warning">
                          {t('platformConfig.notSeededHint', { documented: definition.documentedDefault })}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      <span
                        className="text-sm font-medium text-foreground"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {renderValue(definition, definition.effectiveValue)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setTarget({
                          definition,
                          companyId: null,
                          companyName: null,
                          currentValue: definition.platformValue,
                          inheritedValue: definition.documentedDefault,
                          currentNote: definition.note,
                        })}
                      >
                        {t('platformConfig.editDefault')}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* ── Level 2: per-company overrides ───────────────────────────── */}
            <section className="flex flex-col gap-1">
              <h2 className="text-lg font-medium text-foreground">{t('platformConfig.companyHeading')}</h2>
              <p className="max-w-[70ch] text-sm text-muted-foreground">{t('platformConfig.companySubheading')}</p>

              <div className="mt-4">
                <Table aria-label={t('platformConfig.companyHeading')}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('Company')}</TableHead>
                      {console_.definitions.map(definition => (
                        <TableHead key={definition.key} className="text-center">{t(definition.labelKey)}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {console_.companies.map(company => (
                      <TableRow key={company.companyId}>
                        <TableCell>
                          <span className="font-medium text-foreground">{company.companyName}</span>
                        </TableCell>
                        {console_.definitions.map(definition => {
                          const override = company.overrides[definition.key] ?? null;
                          const effective = company.effective[definition.key] ?? definition.effectiveValue;
                          return (
                            <TableCell key={definition.key} className="text-center">
                              <button
                                type="button"
                                onClick={() => setTarget({
                                  definition,
                                  companyId: company.companyId,
                                  companyName: company.companyName,
                                  currentValue: override,
                                  inheritedValue: definition.effectiveValue,
                                  currentNote: company.notes[definition.key] ?? null,
                                })}
                                className="inline-flex flex-col items-center gap-0.5 rounded px-2 py-1 text-sm transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                              >
                                <span
                                  className={override === null ? 'text-muted-foreground' : 'font-medium text-foreground'}
                                  style={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                  {renderValue(definition, effective)}
                                </span>
                                {/* 🔴 The tristate, said out loud. Without this line an inherited
                                    value and an override that happens to equal it are the same
                                    pixels, and "reset this tenant" becomes unfindable. */}
                                <span className="text-[11px] text-muted-foreground">
                                  {override === null ? t('platformConfig.inherited') : t('platformConfig.overridden')}
                                </span>
                              </button>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>
          </div>
        ) : null}
      </div>

      <PlatformSettingDialog
        open={target !== null}
        definition={target?.definition ?? null}
        companyId={target?.companyId ?? null}
        companyName={target?.companyName ?? null}
        currentValue={target?.currentValue ?? null}
        inheritedValue={target?.inheritedValue ?? ''}
        currentNote={target?.currentNote ?? null}
        onClose={() => setTarget(null)}
        onSaved={load}
      />
    </PageLayout>
  );
};

export default PlatformSettings;
